import React, { useCallback, useState, useEffect, useRef } from "react";
import ReactFlow, {
  Background,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Handle,
  Position,
  getBezierPath,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import {
  Button,
  Form,
  Card,
  Container,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { FaWhatsapp, FaTrash, FaSave, FaTimes } from "react-icons/fa";
import ChatBotflowApi from "../../api/ChatBotflowApi";
import { useNavigate, useLocation } from "react-router-dom";
import * as constants from "../../constants/CONSTANT";
import jwt_decode from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Custom Edge Component with Delete Button
const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
  selected,
  onEdgeDelete,
}) => {
  const [edgePathParams, setEdgePathParams] = useState(null);
  const [showDeleteButton, setShowDeleteButton] = useState(false);

  // Get edge path params from bezier or smooth step function
  useEffect(() => {
    // Choose either bezier or smooth step path
    const params = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
    setEdgePathParams(params);
  }, [sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition]);

  if (!edgePathParams) return null;

  const [edgePath] = edgePathParams;
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  return (
    <g
      onMouseEnter={() => setShowDeleteButton(true)}
      onMouseLeave={() => setShowDeleteButton(false)}
      className="react-flow__edge"
    >
      <path
        id={id}
        style={{ ...style, strokeWidth: selected ? 2 : 1 }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      {showDeleteButton && (
        <foreignObject
          width={20}
          height={20}
          x={midX - 10}
          y={midY - 10}
          className="edgebutton-foreignobject"
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <div>
            <button
              className="edgebutton"
              onClick={(event) => {
                event.stopPropagation();
                onEdgeDelete(id);
              }}
            >
              <FaTimes />
            </button>
          </div>
        </foreignObject>
      )}
    </g>
  );
};

// Node types for sidebar
const nodeTypesList = [
  {
    type: "text",
    label: "Text Message",
    icon: "📝",
  },
  {
    type: "image",
    label: "Image",
    icon: "🖼️",
  },
  {
    type: "video",
    label: "Video",
    icon: "📹",
  },
  {
    type: "document",
    label: "Document",
    icon: "📄",
  },
  {
    type: "flow",
    label: "Flow",
    icon: "🌊",
  },
];

const TextMessageNode = ({ data, id }) => (
  <div className="node text-message-node">
    <Handle type="target" position={Position.Top} />
    <div className="node-header">
      <span className="node-icon">📝</span>
      <span className="node-title">Text Message</span>
      <Button
        variant="danger"
        size="sm"
        className="ms-auto delete-node-btn"
        onClick={(e) => {
          e.stopPropagation();
          data.onDelete(id);
        }}
      >
        <FaTrash />
      </Button>
    </div>
    <div className="node-content">
      {/* Header */}
      {data.header && (
        <div className="section-box mb-2">
          <div className="message-box">{data.header}</div>
        </div>
      )}

      {/* Body */}
      {data.label && (
        <div className="section-box mb-2">
          <div className="message-box">{data.label}</div>
        </div>
      )}

      {/* Footer */}
      {data.footer && (
        <div className="section-box mb-2">
          <div className="message-box">{data.footer}</div>
        </div>
      )}

      {/* Buttons - Only show handles if buttons have data */}
      {data.buttons?.length > 0 &&
        data.buttons.some((btn) => btn.label?.trim()) && (
          <div className="section-box mb-2 d-flex flex-column gap-2">
            {data.buttons.map((btn, idx) => {
              if (!btn.label?.trim()) return null;
              return (
                <div
                  key={idx}
                  className="position-relative message-box px-3 py-1 d-flex align-items-center"
                >
                  {btn.label}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`button-${idx}`}
                    style={{
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

      {/* List Items - Only show handles if list items have data */}
      {data.listItems?.length > 0 &&
        data.listItems.some((item) => item.title?.trim()) && (
          <div className="section-box mb-2">
            {data.section && (
              <div className="message-box mb-2">{data.section}</div>
            )}
            {data.listItems.map((item, idx) => {
              if (!item.title?.trim()) return null;
              return (
                <div
                  key={idx}
                  className="position-relative message-box mb-2 d-flex align-items-center"
                >
                  <input
                    type="radio"
                    name="list-item"
                    id={`radio-${idx}`}
                    className="me-2"
                  />
                  <div>
                    <strong>{item.title}</strong>
                    <div>{item.description}</div>
                  </div>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`list-${idx}`}
                    style={{
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
    </div>
  </div>
);

const ImageNode = ({ data, id }) => (
  <div className="node media-node">
    <Handle type="target" position={Position.Top} />
    <div className="node-header">
      <span className="node-icon">🖼️</span>
      <span className="node-title">Image</span>
      <Button
        variant="danger"
        size="sm"
        className="ms-auto delete-node-btn"
        onClick={(e) => {
          e.stopPropagation();
          data.onDelete(id);
        }}
      >
        <FaTrash />
      </Button>
    </div>
    <div className="node-content">
      {data.fileUrl && (
        <div className="media-preview">
          <img src={data.fileUrl} alt="Preview" className="preview-image" />
        </div>
      )}
      {data.caption && (
        <div className="caption">
          <span className="caption-text">{data.caption}</span>
        </div>
      )}

      {/* Buttons - Only show handles if buttons have data */}
      {data.buttons?.length > 0 &&
        data.buttons.some((btn) => btn.label?.trim()) && (
          <div className="section-box mb-2 d-flex flex-column gap-2">
            {data.buttons.map((btn, idx) => {
              if (!btn.label?.trim()) return null;
              return (
                <div
                  key={idx}
                  className="position-relative message-box px-3 py-1 d-flex align-items-center"
                >
                  {btn.label}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`button-${idx}`}
                    style={{
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

      {/* List Items - Only show handles if list items have data */}
      {data.listItems?.length > 0 &&
        data.listItems.some((item) => item.title?.trim()) && (
          <div className="section-box mb-2">
            {data.section && (
              <div className="message-box mb-2">{data.section}</div>
            )}
            {data.listItems.map((item, idx) => {
              if (!item.title?.trim()) return null;
              return (
                <div
                  key={idx}
                  className="position-relative message-box mb-2 d-flex align-items-center"
                >
                  <input
                    type="radio"
                    name="list-item"
                    id={`radio-${idx}`}
                    className="me-2"
                  />
                  <div>
                    <strong>{item.title}</strong>
                    <div>{item.description}</div>
                  </div>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`list-${idx}`}
                    style={{
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
    </div>
  </div>
);

const VideoNode = ({ data, id }) => (
  <div className="node media-node">
    <Handle type="target" position={Position.Top} />
    <div className="node-header">
      <span className="node-icon">📹</span>
      <span className="node-title">Video</span>
      <Button
        variant="danger"
        size="sm"
        className="ms-auto delete-node-btn"
        onClick={(e) => {
          e.stopPropagation();
          data.onDelete(id);
        }}
      >
        <FaTrash />
      </Button>
    </div>
    <div className="node-content">
      {data.fileUrl && (
        <div className="media-preview">
          <video controls className="preview-video">
            <source src={data.fileUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      )}
      {data.caption && (
        <div className="caption">
          <span className="caption-text">{data.caption}</span>
        </div>
      )}

      {/* Buttons - Only show handles if buttons have data */}
      {data.buttons?.length > 0 &&
        data.buttons.some((btn) => btn.label?.trim()) && (
          <div className="section-box mb-2 d-flex flex-column gap-2">
            {data.buttons.map((btn, idx) => {
              if (!btn.label?.trim()) return null;
              return (
                <div
                  key={idx}
                  className="position-relative message-box px-3 py-1 d-flex align-items-center"
                >
                  {btn.label}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`button-${idx}`}
                    style={{
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

      {/* List Items - Only show handles if list items have data */}
      {data.listItems?.length > 0 &&
        data.listItems.some((item) => item.title?.trim()) && (
          <div className="section-box mb-2">
            {data.section && (
              <div className="message-box mb-2">{data.section}</div>
            )}
            {data.listItems.map((item, idx) => {
              if (!item.title?.trim()) return null;
              return (
                <div
                  key={idx}
                  className="position-relative message-box mb-2 d-flex align-items-center"
                >
                  <input
                    type="radio"
                    name="list-item"
                    id={`radio-${idx}`}
                    className="me-2"
                  />
                  <div>
                    <strong>{item.title}</strong>
                    <div>{item.description}</div>
                  </div>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`list-${idx}`}
                    style={{
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
    </div>
  </div>
);

const DocNode = ({ data, id }) => (
  <div className="node media-node">
    <Handle type="target" position={Position.Top} />
    <div className="node-header">
      <span className="node-icon">📄</span>
      <span className="node-title">Document</span>
      <Button
        variant="danger"
        size="sm"
        className="ms-auto delete-node-btn"
        onClick={(e) => {
          e.stopPropagation();
          data.onDelete(id);
        }}
      >
        <FaTrash />
      </Button>
    </div>
    <div className="node-content">
      {data.fileName && (
        <div className="document-preview">
          <span className="file-name">{data.fileName}</span>
        </div>
      )}
      {data.caption && (
        <div className="caption">
          <span className="caption-text">{data.caption}</span>
        </div>
      )}

      {/* Buttons - Only show handles if buttons have data */}
      {data.buttons?.length > 0 &&
        data.buttons.some((btn) => btn.label?.trim()) && (
          <div className="section-box mb-2 d-flex flex-column gap-2">
            {data.buttons.map((btn, idx) => {
              if (!btn.label?.trim()) return null;
              return (
                <div
                  key={idx}
                  className="position-relative message-box px-3 py-1 d-flex align-items-center"
                >
                  {btn.label}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`button-${idx}`}
                    style={{
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

      {/* List Items - Only show handles if list items have data */}
      {data.listItems?.length > 0 &&
        data.listItems.some((item) => item.title?.trim()) && (
          <div className="section-box mb-2">
            {data.section && (
              <div className="message-box mb-2">{data.section}</div>
            )}
            {data.listItems.map((item, idx) => {
              if (!item.title?.trim()) return null;
              return (
                <div
                  key={idx}
                  className="position-relative message-box mb-2 d-flex align-items-center"
                >
                  <input
                    type="radio"
                    name="list-item"
                    id={`radio-${idx}`}
                    className="me-2"
                  />
                  <div>
                    <strong>{item.title}</strong>
                    <div>{item.description}</div>
                  </div>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`list-${idx}`}
                    style={{
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
    </div>
  </div>
);

const FlowNode = ({ data, id }) => (
  <div className="node text-message-node">
    <Handle type="target" position={Position.Top} />
    <div className="node-header">
      <span className="node-icon">🌊</span>
      <span className="node-title">Flow</span>
      <Button
        variant="danger"
        size="sm"
        className="ms-auto delete-node-btn"
        onClick={(e) => {
          e.stopPropagation();
          data.onDelete(id);
        }}
      >
        <FaTrash />
      </Button>
    </div>
    <div className="node-content">
      {/* Header */}
      {data.header && (
        <div className="section-box mb-2">
          <div className="message-box">{data.header}</div>
        </div>
      )}

      {/* Body */}
      {data.label && (
        <div className="section-box mb-2">
          <div className="message-box">{data.label}</div>
        </div>
      )}

      {/* Flow Button */}
      {data.flow_button_label && (
        <div className="section-box mb-2 d-flex flex-column gap-2">
          <div className="position-relative message-box px-3 py-1 d-flex align-items-center">
            {data.flow_button_label}
            <Handle
              type="source"
              position={Position.Right}
              id={`flow-button-0`}
              style={{
                top: "50%",
                right: -6,
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      )}
    </div>
  </div>
);
const StartNode = ({ data }) => (
  <div className="node start-node">
    <div className="node-header">
      <span className="node-icon">🚀</span>
      <span className="node-title">Flow Start</span>
    </div>
    <div className="node-content">
      <div className="keywords-container">
        {data.keywords?.map(
          (keyword, idx) =>
            keyword && (
              <div key={idx} className="keyword-tag">
                <span className="keyword-value">{keyword}</span>
              </div>
            ),
        )}
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} />
  </div>
);

const nodeTypes = {
  text: TextMessageNode,
  image: ImageNode,
  video: VideoNode,
  document: DocNode,
  flow: FlowNode,
  start: StartNode,
};

// Initial flow start node
const initialNodes = [
  {
    id: "start",
    type: "start",
    position: { x: 100, y: 100 },
    data: {
      keywords: [""],
      type: "button",
      buttons: [{ label: "" }],
      listItems: [],
    },
  },
];

const initialEdges = [];

function AddChatBot({ selectedWhatsAppSetting }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = location.pathname.includes("/edit");
  const existingFlow = location.state?.flow;
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );
  // Initialize state first
  const [nodes, setNodes, onNodesChange] = useNodesState(
    isEditMode && existingFlow
      ? existingFlow.nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            buttons: node.data.buttons || [],
            listItems: node.data.listItems || [],
            keywords: node.data.keywords || [],
            type: node.data.type || "button",
            header: node.data.header || "",
            label: node.data.label || "",
            footer: node.data.footer || "",
            fileUrl: node.data.fileUrl || "",
            fileName: node.data.fileName || "",
            caption: node.data.caption || "",
            section: node.data.section || "",
          },
        }))
      : initialNodes,
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    isEditMode && existingFlow ? existingFlow.edges : initialEdges,
  );
  const [selectedNode, setSelectedNode] = useState(
    isEditMode && existingFlow ? existingFlow.nodes[0] : initialNodes[0],
  );
  const [flowName, setFlowName] = useState(
    isEditMode && existingFlow ? existingFlow.name : "",
  );
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileErrors, setFileErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);

  // Define handleDeleteNode after state initialization
  const handleDeleteNode = useCallback(
    (nodeId) => {
      // Don't allow deleting the start node
      if (nodeId === "start") return;

      // Remove the node
      setNodes((nds) => nds.filter((node) => node.id !== nodeId));

      // Remove connected edges
      setEdges((eds) =>
        eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
      );

      // Clear selected node if it was the deleted one
      if (selectedNode && selectedNode.id === nodeId) {
        setSelectedNode(nodes.find((node) => node.id !== nodeId) || null);
      }
    },
    [selectedNode, nodes, setNodes, setEdges],
  );

  // Update nodes with onDelete handler after handleDeleteNode is defined
  useEffect(() => {
    // Only update nodes if they don't already have the onDelete handler
    const needsUpdate = nodes.some((node) => !node.data.onDelete);
    if (needsUpdate) {
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          data: {
            ...node.data,
            onDelete: handleDeleteNode,
          },
        })),
      );
    }
  }, [handleDeleteNode, setNodes, nodes]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Clean up any object URLs when component unmounts
      nodes.forEach((node) => {
        if (node.data.fileUrl && node.data.fileUrl.startsWith("blob:")) {
          URL.revokeObjectURL(node.data.fileUrl);
        }
      });
      if (uploadedFile?.url && uploadedFile.url.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedFile.url);
      }
    };
  }, [nodes, uploadedFile]);

  const validFileTypes = {
    document: {
      extensions: ["txt", "xls", "xlsx", "doc", "docx", "ppt", "pptx", "pdf"],
      maxSize: 100,
      mimeTypes: [
        "text/plain",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/pdf",
      ],
    },
    image: { extensions: ["jpeg", "jpg", "png"], maxSize: 5 },
    video: { extensions: ["mp4", "3gpp"], maxSize: 16 },
  };

  const validateFile = (file, type, nodeId) => {
    if (!file) return false;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    const fileSizeMB = file.size / (1024 * 1024);
    const fileTypeKey = type === "document" ? "document" : type;

    if (!validFileTypes[fileTypeKey]) {
      setFileErrors((prev) => ({
        ...prev,
        [nodeId]: "This type of file not supported",
      }));
      toast.error("This type of file not supported", { autoClose: 2000 });
      return false;
    }

    const { extensions, maxSize, mimeTypes } = validFileTypes[fileTypeKey];

    if (!extensions || !Array.isArray(extensions)) {
      setFileErrors((prev) => ({
        ...prev,
        [nodeId]: "Invalid file type configuration",
      }));
      return false;
    }

    if (!extensions.includes(fileExtension)) {
      setFileErrors((prev) => ({
        ...prev,
        [nodeId]: "This type of file not supported",
      }));
      toast.error("This type of file not supported", { autoClose: 2000 });
      return false;
    }

    if (fileSizeMB > maxSize) {
      setFileErrors((prev) => ({
        ...prev,
        [nodeId]: `File too large. Max size: ${maxSize}MB`,
      }));
      return false;
    }

    // Special handling for document types
    if (type === "document") {
      // For TXT files, accept text/plain
      if (fileExtension === "txt") {
        if (file.type !== "text/plain") {
          setFileErrors((prev) => ({
            ...prev,
            [nodeId]: "Selected file is not a valid text file",
          }));
          return false;
        }
      } else {
        // For other document types, check against allowed MIME types
        if (!mimeTypes.includes(file.type)) {
          setFileErrors((prev) => ({
            ...prev,
            [nodeId]: "Selected file is not a valid document",
          }));
          return false;
        }
      }
    } else if (type === "image" && !file.type.startsWith("image/")) {
      setFileErrors((prev) => ({
        ...prev,
        [nodeId]: "Selected file is not a valid image",
      }));
      return false;
    } else if (type === "video" && !file.type.startsWith("video/")) {
      setFileErrors((prev) => ({
        ...prev,
        [nodeId]: "Selected file is not a valid video",
      }));
      return false;
    }

    setFileErrors((prev) => ({ ...prev, [nodeId]: "" }));
    return true;
  };

  // Custom edge type for deleting connections
  const edgeTypes = {
    custom: (props) => (
      <CustomEdge {...props} onEdgeDelete={handleDeleteEdge} />
    ),
  };

  // Handle deleting an edge
  const handleDeleteEdge = useCallback(
    (edgeId) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    },
    [setEdges],
  );

  const onConnect = useCallback(
    (params) => {
      // Create custom edge with animated property
      const newEdge = {
        ...params,
        type: "custom", // Use our custom edge
        animated: true,
        style: { stroke: "#007bff" },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges],
  );

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  // Modify addNode function to include node limit check
  const addNode = (type) => {
    // Check total node limit (excluding start node)
    const totalNodes = nodes.filter((node) => node.type !== "start").length;
    if (totalNodes >= 30) {
      setError("Maximum limit of 30 nodes reached");
      return;
    }

    // Find the start node position
    const startNode = nodes.find((node) => node.type === "start");
    const startPosition = startNode ? startNode.position : { x: 100, y: 100 };

    // Calculate position based on total nodes
    // Position nodes in a semi-circle below the start node
    const angle = totalNodes * 60 * (Math.PI / 180); // 60 degrees between each node
    const radius = 200; // Distance from start node
    const x = startPosition.x + radius * Math.sin(angle);
    const y = startPosition.y + radius * Math.cos(angle);

    let baseData = {
      onDelete: handleDeleteNode,
    };

    if (type === "text") {
      baseData = {
        ...baseData,
        label: "",
        type: "button",
        buttons: [{ label: "" }],
      };
    } else if (["image", "video", "document"].includes(type)) {
      baseData = {
        ...baseData,
        fileUrl: "",
        fileName: "",
        caption: "",
        type: "button",
        buttons: [{ label: "" }],
        listItems: [],
      };
    } else if (type === "flow") {
      baseData = {
        ...baseData,
        header: "",
        label: "",
        flow_id: "",
        flow_button_label: "",
      };
    }

    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      position: { x, y },
      data: baseData,
    };

    setNodes((nds) => [...nds, newNode]);
    setSelectedNode(newNode);
  };

  const updateNodeData = useCallback(
    (field, value) => {
      if (!selectedNode) return;

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === selectedNode.id) {
            const updatedData = { ...n.data };

            if (field === "buttons") {
              updatedData.buttons = value.map((opt, index) => ({
                ...opt,
                value: opt.value || `opt-${Date.now()}-${index}`,
              }));
            } else if (field === "keywords") {
              updatedData.keywords = value;
            } else if (field === "listItems") {
              updatedData.listItems = value;
            } else if (field === "type" && value !== updatedData.type) {
              // Handle type change - clear irrelevant data AND remove old edges
              const oldType = updatedData.type;
              updatedData.type = value;

              if (n.type === "text") {
                // Only apply button/list switching behavior for text nodes
                if (value === "button") {
                  // Clear list data when switching to button type
                  updatedData.listItems = [];
                  updatedData.section = "";
                  // Initialize buttons if empty
                  if (
                    !updatedData.buttons ||
                    updatedData.buttons.length === 0
                  ) {
                    updatedData.buttons = [{ label: "" }];
                  }
                } else if (value === "list") {
                  // Clear button data when switching to list type
                  updatedData.buttons = [];
                  // Initialize list items if empty
                  if (
                    !updatedData.listItems ||
                    updatedData.listItems.length === 0
                  ) {
                    updatedData.listItems = [{ title: "", description: "" }];
                    updatedData.section = "";
                  }
                }
              } else {
                // For non-text nodes, keep the original behavior
                if (
                  value === "button" &&
                  (!updatedData.buttons || updatedData.buttons.length === 0)
                ) {
                  updatedData.buttons = [{ label: "" }];
                }
                if (
                  value === "list" &&
                  (!updatedData.listItems || updatedData.listItems.length === 0)
                ) {
                  updatedData.listItems = [{ title: "", description: "" }];
                  if (!updatedData.section) {
                    updatedData.section = "";
                  }
                }
              }

              // Remove edges connected to old handles when type changes
              setEdges((eds) =>
                eds.filter((edge) => {
                  if (edge.source === n.id) {
                    // If switching from button to list, remove button handles
                    if (oldType === "button" && value === "list") {
                      return !edge.sourceHandle?.startsWith("button-");
                    }
                    // If switching from list to button, remove list handles
                    if (oldType === "list" && value === "button") {
                      return !edge.sourceHandle?.startsWith("list-");
                    }
                  }
                  return true;
                }),
              );
            } else {
              updatedData[field] = value;
            }

            return { ...n, data: updatedData };
          }
          return n;
        }),
      );

      setSelectedNode((prev) => {
        if (prev && prev.id === selectedNode.id) {
          const updatedData = { ...prev.data };

          if (field === "buttons") {
            updatedData.buttons = value;
          } else if (field === "keywords") {
            updatedData.keywords = value;
          } else if (field === "listItems") {
            updatedData.listItems = value;
          } else if (field === "type" && value !== updatedData.type) {
            const oldType = updatedData.type;
            updatedData.type = value;

            if (prev.type === "text") {
              // Only apply button/list switching behavior for text nodes
              if (value === "button") {
                // Clear list data when switching to button type
                updatedData.listItems = [];
                updatedData.section = "";
                // Initialize buttons if empty
                if (!updatedData.buttons || updatedData.buttons.length === 0) {
                  updatedData.buttons = [{ label: "" }];
                }
              } else if (value === "list") {
                // Clear button data when switching to list type
                updatedData.buttons = [];
                // Initialize list items if empty
                if (
                  !updatedData.listItems ||
                  updatedData.listItems.length === 0
                ) {
                  updatedData.listItems = [{ title: "", description: "" }];
                  updatedData.section = "";
                }
              }
            } else {
              // For non-text nodes, keep the original behavior
              if (
                value === "button" &&
                (!updatedData.buttons || updatedData.buttons.length === 0)
              ) {
                updatedData.buttons = [{ label: "" }];
              }
              if (
                value === "list" &&
                (!updatedData.listItems || updatedData.listItems.length === 0)
              ) {
                updatedData.listItems = [{ title: "", description: "" }];
                if (!updatedData.section) {
                  updatedData.section = "";
                }
              }
            }

            // Remove edges connected to old handles when type changes
            setEdges((eds) =>
              eds.filter((edge) => {
                if (edge.source === prev.id) {
                  // If switching from button to list, remove button handles
                  if (oldType === "button" && value === "list") {
                    return !edge.sourceHandle?.startsWith("button-");
                  }
                  // If switching from list to button, remove list handles
                  if (oldType === "list" && value === "button") {
                    return !edge.sourceHandle?.startsWith("list-");
                  }
                }
                return true;
              }),
            );
          } else {
            updatedData[field] = value;
          }

          return { ...prev, data: updatedData };
        }
        return prev;
      });
    },
    [selectedNode, setNodes, setEdges],
  );

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsFileUploading(true);
    setError(null);

    try {
      // Validate file first
      if (!validateFile(file, selectedNode.type, selectedNode.id)) {
        event.target.value = "";
        return;
      }

      // Create a local URL for the file
      const localFileUrl = URL.createObjectURL(file);

      // Upload file to WhatsApp API
      const fileData = new FormData();
      fileData.append("messaging_product", "whatsapp");
      fileData.append("file", file);
      fileData.append("description", selectedNode.data.caption || "");

      const fileResponse = await WhatsAppAPI.createFile(null, fileData);

      if (!fileResponse.success) {
        throw new Error(fileResponse.message || "File upload failed");
      }

      const fileId = fileResponse?.records[0]?.id;
      if (!fileId) {
        throw new Error("No file ID received from server");
      }

      // Store both local and server URLs
      const serverFileUrl = `${constants.FRONTEND_URL}/public/${
        userInfo.tenantcode
      }/attachment/${encodeURIComponent(fileResponse?.records[0]?.title)}`;

      // Batch state updates
      const updates = {
        fileUrl: localFileUrl,
        serverFileUrl: serverFileUrl,
        fileName: file.name,
        fileId: fileId,
      };

      // Update node data in a single operation
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === selectedNode.id) {
            return {
              ...n,
              data: {
                ...n.data,
                ...updates,
              },
            };
          }
          return n;
        }),
      );

      // Update selected node data
      setSelectedNode((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          ...updates,
        },
      }));

      // Clear error for this node on success
      setFileErrors((prev) => ({ ...prev, [selectedNode.id]: "" }));
    } catch (error) {
      // console.error("Error uploading file:", error);
      setError(error.message || "Failed to upload file. Please try again.");
      if (uploadedFile?.url) {
        URL.revokeObjectURL(uploadedFile.url);
      }
      event.target.value = "";
      // Set error for this node
      setFileErrors((prev) => ({
        ...prev,
        [selectedNode.id]:
          error.message || "Failed to upload file. Please try again.",
      }));
    } finally {
      setIsFileUploading(false);
    }
  };

  const handleSave = async () => {
    if (!flowName.trim()) {
      toast.error("Please enter a name for your chatbot flow", {
        autoClose: 1000,
      });
      return;
    }

    if (nodes.length === 0) {
      toast.error("Please add at least one node to your flow", {
        autoClose: 1000,
      });
      return;
    }

    // Validate body text for text nodes
    const hasInvalidTextNodes = nodes.some(
      (node) =>
        node.type === "text" && (!node.data.label || !node.data.label.trim()),
    );
    if (hasInvalidTextNodes) {
      toast.error("Please fill in the body text for all text message nodes", {
        autoClose: 1000,
      });
      return;
    }

    // Validate file uploads and captions for media nodes
    const hasInvalidMediaNodes = nodes.some((node) => {
      if (["image", "video", "document"].includes(node.type)) {
        return !node.data.fileUrl || !node.data.caption?.trim();
      }
      return false;
    });
    if (hasInvalidMediaNodes) {
      toast.error(
        "Please upload a file and add a caption for all media nodes",
        {
          autoClose: 1000,
        },
      );
      return;
    }

    // Validate node connections
    const hasUnconnectedNodes = nodes.some((node) => {
      if (node.type === "start") {
        return !edges.some((edge) => edge.source === node.id);
      }
      return !edges.some((edge) => edge.target === node.id);
    });
    if (hasUnconnectedNodes) {
      toast.error("All nodes must be connected in the flow", {
        autoClose: 1000,
      });
      return;
    }

    // Validate button and list item connections
    const hasInvalidConnections = edges.some((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      if (!sourceNode) return true;

      if (edge.sourceHandle?.startsWith("button-")) {
        const buttonIndex = parseInt(edge.sourceHandle.split("-")[1]);
        const button = sourceNode.data.buttons?.[buttonIndex];
        return !button?.label?.trim();
      }

      if (edge.sourceHandle?.startsWith("list-")) {
        const listIndex = parseInt(edge.sourceHandle.split("-")[1]);
        const listItem = sourceNode.data.listItems?.[listIndex];
        return !listItem?.title?.trim();
      }

      return false;
    });
    if (hasInvalidConnections) {
      toast.error("Cannot connect empty buttons or list items", {
        autoClose: 1000,
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create a mapping of node IDs to their connections
      const nodeConnections = {};

      // Process all edges to build connection map
      edges.forEach((edge) => {
        const sourceId = edge.source;
        const targetId = edge.target;
        const sourceHandleId = edge.sourceHandle || null;

        // Initialize array if it doesn't exist
        if (!nodeConnections[sourceId]) {
          nodeConnections[sourceId] = [];
        }

        // If source handle exists (for buttons or list items), add it to connection info
        if (sourceHandleId && sourceHandleId.startsWith("button-")) {
          const buttonIndex = parseInt(sourceHandleId.split("-")[1], 10);
          nodeConnections[sourceId].push({
            type: "button",
            index: buttonIndex,
            targetId: targetId,
          });
        } else if (sourceHandleId && sourceHandleId.startsWith("list-")) {
          const listIndex = parseInt(sourceHandleId.split("-")[1], 10);
          nodeConnections[sourceId].push({
            type: "list",
            index: listIndex,
            targetId: targetId,
          });
        } else {
          // Default connection (no specific handle)
          nodeConnections[sourceId].push({
            type: "default",
            targetId: targetId,
          });
        }
      });

      // Build the structured flow data
      const flowData = {
        id: isEditMode ? existingFlow.id : Date.now().toString(),
        name: flowName,
        business_number: selectedWhatsAppSetting,
        nodes: nodes.map((node) => ({
          id: node.id,
          type: node.type,
          position: {
            x: node.position.x,
            y: node.position.y,
          },
          data: {
            ...node.data,
            fileUrl: node.data.serverFileUrl || node.data.fileUrl,
            keywords: node.data.keywords || [],
            buttons: (node.data.buttons || []).map((btn) => ({
              label: btn.label || "",
            })),
            listItems: (node.data.listItems || []).map((item) => ({
              title: item.title || "",
              description: item.description || "",
            })),
          },
        })),
        edges: edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle || null,
          targetHandle: edge.targetHandle || null,
        })),
        flow: {},
        status: isEditMode ? existingFlow.status : "active",
      };

      // Process Start Node
      const startNode = nodes.find((node) => node.type === "start");
      if (startNode) {
        flowData.flow.start = {
          id: startNode.id,
          type: "start",
          keywords: startNode.data.keywords || [],
          next_connectors: (nodeConnections[startNode.id] || []).map(
            (conn) => ({
              type: conn.type,
              index: conn.index || null,
              targetId: conn.targetId,
            }),
          ),
        };
      }

      // Process all other nodes
      nodes.forEach((node) => {
        if (node.type === "start") return;

        const nodeData = {
          id: node.id,
          type: node.type,
          next_connectors: (nodeConnections[node.id] || []).map((conn) => ({
            type: conn.type,
            index: conn.index || null,
            targetId: conn.targetId,
          })),
        };

        // Add type-specific data
        if (node.type === "text") {
          nodeData.header = node.data.header || "";
          nodeData.body_text = node.data.label || "";
          nodeData.footer_text = node.data.footer || "";
          nodeData.action_type = "interactive";
          nodeData.interactive_type = node.data.type || "button";

          // Process buttons for text nodes - SAFE VERSION
          if (node.data.type === "button" && node.data.buttons) {
            const validButtons = node.data.buttons.filter(
              (button) => button.label && button.label.trim(),
            );

            nodeData.interactive_buttons = validButtons.map((button, index) => {
              // SAFE: Ensure connections is an array
              const connections = (nodeConnections[node.id] || []).filter(
                (conn) => conn.type === "button" && conn.index === index,
              );

              const nextConnectorIds = connections
                .filter((conn) => conn.targetId)
                .map((conn) => conn.targetId);

              return {
                id: `btn-${node.id}-${index}`,
                keywords: button.label.trim(),
                next_connector_ids: nextConnectorIds,
                next_connector_id: nextConnectorIds[0] || null,
              };
            });
          }

          // Process list items for text nodes - SAFE VERSION
          if (node.data.type === "list" && node.data.listItems) {
            nodeData.sections = [
              {
                title: node.data.section || "",
                rows: node.data.listItems.map((item, index) => {
                  // SAFE: Ensure connections is an array for list items
                  const connections = (nodeConnections[node.id] || []).filter(
                    (conn) => conn.type === "list" && conn.index === index,
                  );

                  const nextConnectorIds = connections
                    .filter((conn) => conn.targetId)
                    .map((conn) => conn.targetId);

                  return {
                    id: `list-${node.id}-${index}`,
                    keywords: item.title || "",
                    description: item.description || "",
                    next_connector_ids: nextConnectorIds,
                    next_connector_id: nextConnectorIds[0] || null,
                  };
                }),
              },
            ];
          }
        } else if (["image", "video", "document"].includes(node.type)) {
          // Media nodes
          nodeData.action_type = node.type;
          nodeData.file_url =
            node.data.serverFileUrl || node.data.fileUrl || "";
          nodeData.file_name = node.data.fileName || "";
          nodeData.caption = node.data.caption || "";
          nodeData.interactive_type = node.data.type || "button";
          nodeData.fileid = node.data.fileId || null;

          // Process buttons for media nodes - SAFE VERSION
          if (node.data.type === "button" && node.data.buttons) {
            const validButtons = node.data.buttons.filter(
              (button) => button.label && button.label.trim(),
            );

            nodeData.interactive_buttons = validButtons.map((button, index) => {
              // SAFE: Ensure connections is an array
              const connections = (nodeConnections[node.id] || []).filter(
                (conn) => conn.type === "button" && conn.index === index,
              );

              const nextConnectorIds = connections
                .filter((conn) => conn.targetId)
                .map((conn) => conn.targetId);

              return {
                id: `btn-${node.id}-${index}`,
                keywords: button.label.trim(),
                next_connector_ids: nextConnectorIds,
                next_connector_id: nextConnectorIds[0] || null,
              };
            });
          }

          // Process list items for media nodes - SAFE VERSION
          if (node.data.type === "list" && node.data.listItems) {
            nodeData.sections = [
              {
                title: node.data.section || "",
                rows: node.data.listItems.map((item, index) => {
                  // SAFE: Ensure connections is an array for list items
                  const connections = (nodeConnections[node.id] || []).filter(
                    (conn) => conn.type === "list" && conn.index === index,
                  );

                  const nextConnectorIds = connections
                    .filter((conn) => conn.targetId)
                    .map((conn) => conn.targetId);

                  return {
                    id: `list-${node.id}-${index}`,
                    keywords: item.title || "",
                    description: item.description || "",
                    next_connector_ids: nextConnectorIds,
                    next_connector_id: nextConnectorIds[0] || null,
                  };
                }),
              },
            ];
          }

          // Default connection for media nodes
          const defaultConnections = (nodeConnections[node.id] || []).filter(
            (conn) => conn.type === "default",
          );

          nodeData.next_connector_id =
            defaultConnections.length > 0
              ? defaultConnections[0].targetId
              : null;

          nodeData.next_connector_ids = defaultConnections
            .filter((conn) => conn.targetId)
            .map((conn) => conn.targetId);
        } else if (node.type === "flow") {
          nodeData.action_type = "flow";
          nodeData.header = node.data.header || "";
          nodeData.body_text = node.data.label || "";
          nodeData.flow_id = node.data.flow_id || "";
          nodeData.flow_button_label = node.data.flow_button_label || "";

          // Process default connections for flow nodes
          const defaultConnections = (nodeConnections[node.id] || []).filter(
            (conn) => conn.type === "default",
          );

          nodeData.next_connector_id =
            defaultConnections.length > 0
              ? defaultConnections[0].targetId
              : null;
        }

        flowData.flow[node.id] = nodeData;
      });
      let result;
      if (isEditMode) {
        result = await ChatBotflowApi.updateChatbotFlow(flowData.id, flowData);
      } else {
        result = await ChatBotflowApi.createChatbotFlow(flowData);
      }

      if (result.success) {
        toast.success(
          `Flow ${isEditMode ? "updated" : "created"} successfully!`,
          {
            autoClose: 1000,
          },
        );
        setError(null);

        // Navigate back to list after successful save
        setTimeout(() => {
          navigate("/chatbot");
        }, 1500);
      } else {
        // Display the error message from the backend
        toast.error(
          result.error ||
            result.message ||
            `Failed to ${isEditMode ? "update" : "create"} flow`,
          {
            autoClose: 1000,
          },
        );
      }
    } catch (error) {
      // console.error("Error saving flow:", error);
      toast.error(
        error.message ||
          `Failed to ${
            isEditMode ? "update" : "create"
          } flow. Please try again.`,
        {
          autoClose: 1000,
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderResponseType = () => {
    // For non-text nodes, show only buttons
    if (selectedNode.type !== "text") {
      return (
        <Form.Group className="mb-3">
          <Form.Label>Buttons</Form.Label>
          {(selectedNode.data.buttons || []).map((btn, index) => (
            <div key={index} className="d-flex mb-2">
              <div className="flex-grow-1">
                <Form.Control
                  value={btn.label || ""}
                  maxLength={20}
                  onChange={(e) => {
                    const newButtons = [...selectedNode.data.buttons];
                    newButtons[index] = { ...btn, label: e.target.value };
                    updateNodeData("buttons", newButtons);
                  }}
                  placeholder={`Button ${index + 1} (max 20 chars)`}
                />
              </div>
              <Button
                variant="danger"
                size="sm"
                className="ms-2"
                onClick={() => {
                  const newButtons = selectedNode.data.buttons.filter(
                    (_, i) => i !== index,
                  );
                  updateNodeData("buttons", newButtons);
                }}
              >
                <FaTrash />
              </Button>
            </div>
          ))}
          {(selectedNode.data.buttons?.length || 0) < 3 && (
            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2"
              onClick={() => {
                const newButtons = [
                  ...(selectedNode.data.buttons || []),
                  { label: "" },
                ];
                updateNodeData("buttons", newButtons);
              }}
            >
              Add Button
            </Button>
          )}
        </Form.Group>
      );
    }

    // For text nodes, show response type selection and corresponding interface
    const type = selectedNode.data.type || "button";

    return (
      <>
        <Form.Group className="mb-3">
          <Form.Label>Response Type</Form.Label>
          <Form.Select
            value={type}
            onChange={(e) => updateNodeData("type", e.target.value)}
          >
            <option value="button">Button</option>
            <option value="list">List</option>
          </Form.Select>
        </Form.Group>

        {type === "button" && (
          <Form.Group className="mb-3">
            {(selectedNode.data.buttons || []).map((btn, index) => (
              <div key={index} className="d-flex mb-2">
                <div className="flex-grow-1">
                  <Form.Control
                    value={btn.label || ""}
                    maxLength={20}
                    onChange={(e) => {
                      const newButtons = [...selectedNode.data.buttons];
                      newButtons[index] = { ...btn, label: e.target.value };
                      updateNodeData("buttons", newButtons);
                    }}
                    placeholder={`Button ${index + 1} (max 20 chars)`}
                  />
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  className="ms-2"
                  onClick={() => {
                    const newButtons = selectedNode.data.buttons.filter(
                      (_, i) => i !== index,
                    );
                    updateNodeData("buttons", newButtons);
                  }}
                >
                  <FaTrash />
                </Button>
              </div>
            ))}
            {(selectedNode.data.buttons?.length || 0) < 3 && (
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  const newButtons = [
                    ...(selectedNode.data.buttons || []),
                    { label: "" },
                  ];
                  updateNodeData("buttons", newButtons);
                }}
              >
                Add Button
              </Button>
            )}
          </Form.Group>
        )}

        {type === "list" && (
          <Form.Group className="mb-3">
            <Form.Label>List Items</Form.Label>

            {/* Section Title - only one */}
            <Form.Control
              maxLength={24}
              className="mb-2"
              placeholder="Section Title (Max 24 chars)"
              value={selectedNode.data.section || ""}
              onChange={(e) => updateNodeData("section", e.target.value)}
            />

            {/* Row Items */}
            {(selectedNode.data.listItems || []).map((item, index) => (
              <div key={index} className="border p-2 mb-2 rounded">
                <Form.Control
                  maxLength={24}
                  required
                  className="mb-1"
                  placeholder="Row Title (Max 24 chars)"
                  value={item.title || ""}
                  onChange={(e) => {
                    const newItems = [...(selectedNode.data.listItems || [])];
                    newItems[index] = {
                      ...newItems[index],
                      title: e.target.value,
                    };
                    updateNodeData("listItems", newItems);
                  }}
                />
                <Form.Control
                  maxLength={72}
                  className="mb-1"
                  placeholder="Row Description (Max 72 chars)"
                  value={item.description || ""}
                  onChange={(e) => {
                    const newItems = [...(selectedNode.data.listItems || [])];
                    newItems[index] = {
                      ...newItems[index],
                      description: e.target.value,
                    };
                    updateNodeData("listItems", newItems);
                  }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    const newItems = selectedNode.data.listItems.filter(
                      (_, i) => i !== index,
                    );
                    updateNodeData("listItems", newItems);
                  }}
                >
                  <FaTrash /> Remove
                </Button>
              </div>
            ))}

            {(selectedNode.data.listItems?.length || 0) < 10 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newItems = [
                    ...(selectedNode.data.listItems || []),
                    {
                      title: "",
                      description: "",
                    },
                  ];
                  updateNodeData("listItems", newItems);
                }}
              >
                Add List Item
              </Button>
            )}
          </Form.Group>
        )}
      </>
    );
  };

  const renderHeaderInput = () => {
    if (!selectedNode || selectedNode.type === "start") return null;

    const isMediaType = ["image", "video", "document"].includes(
      selectedNode.type,
    );
    const label = isMediaType
      ? `Upload ${selectedNode.type} *`
      : "Header (Max 20 chars)";
    const inputField = isMediaType ? (
      <>
        <div className="file-upload-container">
          <Form.Control
            type="file"
            onChange={handleFileUpload}
            accept={
              selectedNode.type === "image"
                ? "image/jpeg,image/jpg,image/png"
                : selectedNode.type === "video"
                  ? "video/mp4,video/3gpp"
                  : ".txt,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf"
            }
            className="file-input"
            id={`file-upload-${selectedNode.id}`}
            required
          />
          <label
            htmlFor={`file-upload-${selectedNode.id}`}
            className="file-label"
          >
            {selectedNode.data.fileName || `Choose ${selectedNode.type} file *`}
          </label>
        </div>
        {/* Show only relevant file error for the current node type and node id */}
        {fileErrors[selectedNode.id] &&
          fileErrors[selectedNode.id].length > 0 && (
            <Form.Text className="text-danger">
              {(() => {
                if (selectedNode.type === "image") {
                  return fileErrors[selectedNode.id].replace(
                    /Allowed types: .*/,
                    "Allowed: jpeg, jpg, png",
                  );
                } else if (selectedNode.type === "video") {
                  return fileErrors[selectedNode.id].replace(
                    /Allowed types: .*/,
                    "Allowed: mp4, 3gpp",
                  );
                } else if (selectedNode.type === "document") {
                  return fileErrors[selectedNode.id].replace(
                    /Allowed types: .*/,
                    "Allowed: txt, xls, xlsx, doc, docx, ppt, pptx, pdf",
                  );
                }
                return fileErrors[selectedNode.id];
              })()}
            </Form.Text>
          )}
        <Form.Text className="text-muted">
          {selectedNode.type === "image" &&
            "Images only (JPEG, JPG, PNG) - Max 5MB"}
          {selectedNode.type === "video" &&
            "Videos only (MP4, 3GPP) - Max 16MB"}
          {selectedNode.type === "document" &&
            "Documents only (TXT, XLS, XLSX, DOC, DOCX, PPT, PPTX, PDF) - Max 100MB"}
        </Form.Text>
        {selectedNode.data.fileUrl && selectedNode.type === "image" && (
          <div className="mt-2 media-preview">
            <img
              src={selectedNode.data.fileUrl}
              alt="Preview"
              className="preview-image"
              style={{ maxHeight: "200px", objectFit: "contain" }}
            />
          </div>
        )}
        {selectedNode.data.fileUrl && selectedNode.type === "video" && (
          <div className="mt-2 media-preview">
            <video
              controls
              className="preview-video"
              style={{ maxHeight: "200px" }}
            >
              <source src={selectedNode.data.fileUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}
        {selectedNode.data.fileName && selectedNode.type === "document" && (
          <div className="mt-2 document-preview">
            <div className="document-icon">📄</div>
            <div className="document-info">
              <div className="file-name">{selectedNode.data.fileName}</div>
              <div className="file-type">
                {selectedNode.data.fileName.split(".").pop().toUpperCase()}
              </div>
            </div>
          </div>
        )}
      </>
    ) : (
      <Form.Control
        maxLength={20}
        value={selectedNode.data.header || ""}
        onChange={(e) => updateNodeData("header", e.target.value)}
        placeholder="Enter header..."
      />
    );

    return (
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        {inputField}
      </Form.Group>
    );
  };

  const renderBodyOrCaption = () => {
    if (!selectedNode || selectedNode.type === "start") return null;

    const isMediaType = ["image", "video", "document"].includes(
      selectedNode.type,
    );
    const label =
      selectedNode.type === "text" || selectedNode.type === "flow"
        ? "Body (required, max 1024 chars)"
        : "Caption *";
    const placeholder =
      selectedNode.type === "text" || selectedNode.type === "flow"
        ? "Enter your message..."
        : "Enter caption...";
    const dataKey =
      selectedNode.type === "text" || selectedNode.type === "flow"
        ? "label"
        : "caption";

    return (
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <Form.Control
          required
          as="textarea"
          rows={3}
          maxLength={selectedNode.type === "text" ? 1024 : undefined}
          value={selectedNode.data[dataKey] || ""}
          onChange={(e) => updateNodeData(dataKey, e.target.value)}
          placeholder={placeholder}
        />
      </Form.Group>
    );
  };

  const renderFooterInput = () => {
    if (!selectedNode || selectedNode.type !== "text") return null;

    return (
      <Form.Group className="mb-3">
        <Form.Label>Footer (Max 60 chars)</Form.Label>
        <Form.Control
          maxLength={60}
          value={selectedNode.data.footer || ""}
          onChange={(e) => updateNodeData("footer", e.target.value)}
          placeholder="Enter footer..."
        />
      </Form.Group>
    );
  };

  const renderStartNodeKeywords = () => {
    if (!selectedNode || selectedNode.type !== "start") return null;

    const keywords = selectedNode.data.keywords || [];

    return (
      <Form.Group className="mb-3">
        <Form.Label>Keywords (e.g. hi, hello, hey)</Form.Label>
        {keywords.map((kw, index) => (
          <div key={index} className="d-flex mb-2">
            <Form.Control
              value={kw}
              onChange={(e) => {
                const updatedKeywords = [...keywords];
                updatedKeywords[index] = e.target.value;
                updateNodeData("keywords", updatedKeywords);
              }}
              placeholder={`Keyword ${index + 1}`}
            />
            <Button
              variant="danger"
              size="sm"
              className="ms-2"
              onClick={() => {
                const updatedKeywords = keywords.filter((_, i) => i !== index);
                updateNodeData("keywords", updatedKeywords);
              }}
            >
              <FaTrash />
            </Button>
          </div>
        ))}
        {keywords.length < 10 && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              const updatedKeywords = [...keywords, ""];
              updateNodeData("keywords", updatedKeywords);
            }}
          >
            Add Keyword
          </Button>
        )}
      </Form.Group>
    );
  };

  const renderFlowProperties = () => {
    if (!selectedNode || selectedNode.type !== "flow") return null;
    return (
      <>
        <Form.Group className="mb-3">
          <Form.Label>Flow ID</Form.Label>
          <Form.Control
            value={selectedNode.data.flow_id || ""}
            onChange={(e) => updateNodeData("flow_id", e.target.value)}
            placeholder="Enter flow ID..."
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Flow Button Label</Form.Label>
          <Form.Control
            value={selectedNode.data.flow_button_label || ""}
            onChange={(e) =>
              updateNodeData("flow_button_label", e.target.value)
            }
            placeholder="Enter flow button label..."
          />
        </Form.Group>
      </>
    );
  };

  const renderNodeProperties = () => {
    if (!selectedNode) return null;

    // If selected node is "start", show only the keyword field
    if (selectedNode.type === "start") {
      return renderStartNodeKeywords();
    }
    if (selectedNode.type === "flow") {
      return (
        <>
          {renderHeaderInput()}
          {renderBodyOrCaption()}
          {renderFlowProperties()}
        </>
      );
    }

    return (
      <>
        {renderHeaderInput()}
        {renderBodyOrCaption()}
        {renderFooterInput()}
        {renderResponseType()}
      </>
    );
  };

  return (
    <ReactFlowProvider>
      <Container fluid className="p-4">
        <ToastContainer position="top-right" />
        {isFileUploading && (
          <div className="file-upload-overlay">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Uploading...</span>
            </div>
            <p className="mt-2">Uploading file...</p>
          </div>
        )}
        <Row>
          <Col>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FaWhatsapp className="me-2" size={24} />
                  <h4 className="mb-0">
                    {isEditMode
                      ? "Edit Chatbot Flow"
                      : "Create New Chatbot Flow"}
                  </h4>
                </div>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Enter Flow Name"
                    value={flowName}
                    onChange={(e) => setFlowName(e.target.value)}
                    style={{ width: "300px" }}
                  />
                  <Button variant="success" onClick={handleSave}>
                    <FaSave className="me-2" />
                    {isEditMode ? "Update Flow" : "Save Flow"}
                  </Button>
                  <Button onClick={() => navigate(`/chatbot`)}>Back</Button>
                </div>
              </Card.Header>
              <Card.Body>
                {isLoading && (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading...</p>
                  </div>
                )}
                {!isLoading && (
                  <div style={{ display: "flex", height: "600px" }}>
                    {/* Sidebar */}
                    <div className="node-palette">
                      <h5>Content Blocks</h5>
                      {nodeTypesList.map((block) => {
                        const totalNodes = nodes.filter(
                          (node) => node.type !== "start",
                        ).length;
                        const isDisabled = totalNodes >= 30;

                        return (
                          <div
                            key={block.type}
                            className={`node-type ${
                              isDisabled ? "disabled" : ""
                            }`}
                            onClick={() => !isDisabled && addNode(block.type)}
                          >
                            <span className="node-type-icon">{block.icon}</span>
                            <div className="node-type-info">
                              <span className="node-type-label">
                                {block.label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {nodes.filter((node) => node.type !== "start").length >=
                        30 && (
                        <div className="node-limit-message">
                          <Alert variant="warning" className="mt-3 mb-0">
                            <small>
                              Maximum limit of 30 nodes reached. Please remove
                              some nodes to add more.
                            </small>
                          </Alert>
                        </div>
                      )}
                    </div>

                    {/* Flow Canvas */}
                    <div style={{ flex: 1, position: "relative" }}>
                      <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeClick={onNodeClick}
                        nodeTypes={nodeTypes}
                        fitView
                        proOptions={{ hideAttribution: true }}
                      >
                        {/* <MiniMap /> */}
                        {/* <Controls /> */}
                        <Background gap={8} />
                      </ReactFlow>
                    </div>

                    {/* Node Properties Panel */}
                    {selectedNode && (
                      <div
                        className={`node-properties ${
                          selectedNode.type === "start"
                            ? "start-node-properties"
                            : ""
                        }`}
                      >
                        <h5>Node Properties</h5>
                        <div className="my-2 text-primary space-y-1">
                          <div>
                            💡 <strong>Tips:</strong>
                          </div>
                          <div className="ml-4">
                            • Use the <code>default</code> keyword to set a
                            fallback message. If no active bot is found for a
                            conversation, the system will automatically send
                            this default message to the client.
                          </div>
                          <div className="ml-4">
                            • Use the <code>newlead</code> keyword to set a
                            welcome message for new leads. This will only
                            trigger for first-time customers who have no
                            existing conversation history.
                          </div>
                        </div>
                        {renderNodeProperties()}
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .node-palette {
          width: 250px;
          padding: 16px;
          background-color: #f8f9fa;
          border-right: 1px solid #dee2e6;
        }
        .node-type {
          padding: 12px;
          margin-bottom: 8px;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
        }
        .node-type:hover {
          background: #f8f9fa;
          border-color: #adb5bd;
          transform: translateX(5px);
        }
        .node-type-icon {
          font-size: 1.25rem;
          margin-right: 8px;
        }
        .node-type-info {
          display: flex;
          flex-direction: column;
        }
        .node-type-label {
          font-weight: 500;
        }
        .node-properties {
          width: 300px;
          padding: 16px;
          background-color: #f8f9fa;
          border-left: 1px solid #dee2e6;
          transition: all 0.3s ease;
          overflow-y: auto;
          max-height: 600px;
        }
        .start-node-properties {
          background-color: #e9ecef;
        }
        .start-node-properties h5 {
          color: #495057;
        }
        .node {
          padding: 10px;
          border-radius: 4px;
          background: white;
          border: 1px solid #dee2e6;
          min-width: 150px;
          width: 230px;
        }
        .node-header {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid #dee2e6;
        }
        .node-icon {
          margin-right: 8px;
          font-size: 1.25rem;
        }
        .node-title {
          font-weight: 500;
        }
        .node-content {
          font-size: 0.9rem;
          padding: 8px;
        }
        .message-text {
          margin-bottom: 8px;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .keywords-container {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 8px;
        }
        .keyword-tag {
          padding: 4px 8px;
          background: #e9ecef;
          border-radius: 4px;
          font-size: 0.8rem;
        }
        .keyword-value {
          color: #0d6efd;
        }
        .options-container {
          margin-top: 8px;
        }
        .quick-reply-option {
          padding: 4px 8px;
          margin: 4px 0;
          background: #f8f9fa;
          border-radius: 4px;
          font-size: 0.8rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .option-number {
          color: #6c757d;
          font-weight: 500;
        }
        .option-text {
          flex: 1;
        }
        .media-preview {
          margin: 8px 0;
          padding: 8px;
          background: #f8f9fa;
          border-radius: 4px;
          text-align: center;
        }
        .preview-image {
          max-width: 100%;
          max-height: 150px;
          object-fit: contain;
          border-radius: 4px;
        }
        .preview-video {
          max-width: 100%;
          max-height: 150px;
          border-radius: 4px;
        }
        .document-preview {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          background: white;
          border-radius: 4px;
        }
        .file-name {
          font-size: 0.8rem;
          color: #495057;
        }
        .caption {
          margin-top: 8px;
          padding: 4px 8px;
          background: #f8f9fa;
          border-radius: 4px;
          font-size: 0.8rem;
        }
        .caption-label {
          font-weight: 500;
          color: #495057;
          margin-right: 4px;
        }
        .caption-text {
          color: #6c757d;
        }
        .message-box {
          padding: 5px;
          background: #f8f9fa;
          border-radius: 4px;
          margin-bottom: 5px;
          max-width: 180px;

          word-break: break-word;
          white-space: normal;
          max-width: 180px; /* or whatever fits your node width */
          overflow-wrap: break-word;
        }
        .section-box {
          margin-bottom: 10px;
        }
        .delete-node-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }
        .node:hover .delete-node-btn {
          opacity: 1;
        }
        .node-type.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #e9ecef;
        }
        .node-type-count {
          font-size: 0.8rem;
          color: #6c757d;
          margin-left: 4px;
        }
        .node-limit-message {
          margin-top: 16px;
          padding: 8px;
          background-color: #fff3cd;
          border-radius: 4px;
          font-size: 0.9rem;
        }
        .node-limit-message small {
          color: #856404;
        }
        .file-upload-container {
          position: relative;
          margin-bottom: 10px;
        }
        .file-input {
          position: absolute;
          left: 0;
          top: 0;
          opacity: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }
        .file-label {
          display: block;
          padding: 8px 12px;
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          cursor: pointer;
          text-align: center;
          color: #495057;
          transition: all 0.2s;
        }
        .file-label:hover {
          background-color: #e9ecef;
        }
        .file-label.required {
          border-color: #dc3545;
        }
        .file-label.required:hover {
          border-color: #dc3545;
          background-color: #fff5f5;
        }
        .document-preview {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 4px;
        }
        .document-icon {
          font-size: 24px;
        }
        .document-info {
          flex: 1;
        }
        .file-upload-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }
        .media-preview {
          position: relative;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 12px;
          text-align: center;
          min-height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .preview-image {
          max-width: 100%;
          max-height: 200px;
          object-fit: contain;
          border-radius: 4px;
          transition: opacity 0.3s ease;
        }
        .preview-video {
          max-width: 100%;
          max-height: 200px;
          border-radius: 4px;
          transition: opacity 0.3s ease;
        }
      `}</style>
    </ReactFlowProvider>
  );
}

export default AddChatBot;
